/**
 * @packageDocumentation
 * @module f1/abstract-data-type
 */

import { t_group, t_key, t_rec_arr } from '../types';
import fold from './fold';
import is_array from '../is/is_array';
import mimic from '../fn/mimic';

type t_group_fn<V> = (v: V, k: t_key, adt: t_rec_arr<V>) => string;

/**
 * Creates an object of elements split into groups defined by the grouping method.
 *
 * @example
 *
 * const [even, odd] = partition(n => n % 2 === 0, [1, 2, 3, 4]); // => even: [2, 4], odd: [1, 3]
 */

export interface Group {
  /**
   * Splits a list by a boolean predicate and places `true` in the first list and `false` in the second list
   *
   * @public
   * @function f1.group
   * @param adt the array or record to partition in two sets
   * @example
   *
   * ```ts
   * f1.group(f1.is.odd, [16, 17, 18]) === { 0: [17], 1: [16, 18] }
   * f1.group((v) => f1.is.odd(v) ? 'odd': 'even', [16, 17, 18]) === { 'odd': [17], 'even': [16, 18] }
   *
   * const s = { 0: 'odd', 1: 'even' };
   * f1.group((v) => f1.strategy(s, f1.is.odd(v)), [16, 17, 18]) === { 'odd': [17], 'even': [16, 18] }
   * // or with a pipe
   * f1.group(f1.pipe([
   *   f1.is.odd,
   *   f1.strategy(s)
   * ]), [16, 17, 18]) === { 'odd': [17], 'even': [16, 18] }
   * ```
   *
   */
  <V>(fn: t_group_fn<V>, adt: t_rec_arr<V>): t_group<V>;
  <V>(fn: t_group_fn<V>): (adt: t_rec_arr<V>) => t_group<V>;
}

/**
 * @see Group
 */
export default <V>(fn: t_group_fn<V>, adt: t_rec_arr<V>): t_group<V> => {
  const isA = is_array(adt);
  const replicate = isA ? [] : {};

  return fold<V, t_group<V>, t_group<V>>(
    (acc, v, k, adt) => {
      const gK = fn(v, k, adt);
      acc[gK] = acc[gK] || mimic(replicate);

      const sK = isA ? (acc[gK] as V[]).length : k;
      acc[gK][sK] = v;
      return acc;
    },
    {},
    adt
  );
};
